import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { Zone, FretNote, GuidanceMode } from "@/models/types";
import { FRET_MIN, FRET_MAX, INLAY_FRETS, DOUBLE_INLAY_FRETS, STRINGS } from "@/data/demoSongs";

const NUM_FRETS = FRET_MAX - FRET_MIN + 1;
const STRING_WEIGHTS = [1.1, 1.4, 1.8, 2.4, 3.0, 3.8]; // high E -> low E

const keyOf = (s: number, f: number) => `${s}-${f}`;
const noteX = (fret: number) => ((fret - FRET_MIN + 0.5) / NUM_FRETS) * 100;
const rowY = (string: number) => ((string - 1 + 0.5) / 6) * 100;

interface FretboardProps {
  zone: Zone | null;
  litNotes: FretNote[];
  ghostNotes?: FretNote[];
  recoveryLevel?: number;
  transitionIntensity?: number;
  mode?: GuidanceMode;
  compact?: boolean;
  onNoteTap?: (note: FretNote) => void;
}

export const Fretboard: React.FC<FretboardProps> = ({
  zone,
  litNotes,
  ghostNotes = [],
  recoveryLevel = 0,
  transitionIntensity = 0,
  mode = "FLOW",
  compact = false,
  onNoteTap,
}) => {
  const litKeys = useMemo(() => new Set(litNotes.map((x) => keyOf(x.string, x.fret))), [litNotes]);
  const litMap = useMemo(() => {
    const m = new Map<string, FretNote>();
    litNotes.forEach((x) => m.set(keyOf(x.string, x.fret), x));
    return m;
  }, [litNotes]);
  const ghostFiltered = useMemo(
    () => ghostNotes.filter((g) => !litKeys.has(keyOf(g.string, g.fret))),
    [ghostNotes, litKeys]
  );

  const size = compact ? "w-5 h-5 text-[9px]" : "w-7 h-7 sm:w-8 sm:h-8 text-[11px]";

  return (
    <div
      data-testid="fretboard-canvas-container"
      className="relative w-full h-full min-h-0 rounded-2xl overflow-hidden select-none"
      style={{
        background:
          "linear-gradient(180deg,#101317 0%,#171b21 48%,#0d0f13 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -18px 40px rgba(0,0,0,0.55)",
        border: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      {/* active zone soft illumination */}
      {zone && recoveryLevel === 0 && (
        <ZoneGlow zone={zone} />
      )}

      {/* fret wires */}
      {Array.from({ length: NUM_FRETS + 1 }).map((_, i) => (
        <div
          key={`wire-${i}`}
          className="absolute top-0 bottom-0"
          style={{
            left: `${(i / NUM_FRETS) * 100}%`,
            width: i === 0 ? 3 : 2,
            background:
              i === 0
                ? "linear-gradient(180deg,#6b7280,#3b424c)"
                : "linear-gradient(180deg,#3a424e,#262c34)",
            boxShadow: "0 0 3px rgba(0,0,0,0.6)",
          }}
        />
      ))}

      {/* inlays */}
      {INLAY_FRETS.filter((f) => f >= FRET_MIN && f <= FRET_MAX).map((f) => (
        <div
          key={`inlay-${f}`}
          data-testid={`fret-marker-${f}`}
          className="absolute rounded-full"
          style={{
            left: `${noteX(f)}%`,
            top: "50%",
            transform: "translate(-50%,-50%)",
            width: compact ? 6 : 9,
            height: compact ? 6 : 9,
            background: "rgba(148,163,184,0.18)",
          }}
        />
      ))}
      {DOUBLE_INLAY_FRETS.filter((f) => f >= FRET_MIN && f <= FRET_MAX).map((f) =>
        [30, 70].map((yp) => (
          <div
            key={`inlay2-${f}-${yp}`}
            data-testid={`fret-marker-${f}`}
            className="absolute rounded-full"
            style={{
              left: `${noteX(f)}%`,
              top: `${yp}%`,
              transform: "translate(-50%,-50%)",
              width: compact ? 6 : 9,
              height: compact ? 6 : 9,
              background: "rgba(148,163,184,0.18)",
            }}
          />
        ))
      )}

      {/* strings */}
      {STRINGS.map((s, i) => (
        <div
          key={`string-${s}`}
          className="absolute left-0 right-0"
          style={{
            top: `${rowY(s)}%`,
            height: STRING_WEIGHTS[i],
            transform: "translateY(-50%)",
            background:
              "linear-gradient(90deg,rgba(161,161,170,0.15),rgba(161,161,170,0.75) 12%,rgba(161,161,170,0.75) 88%,rgba(161,161,170,0.15))",
          }}
        />
      ))}

      {/* ghost (upcoming zone) markers */}
      {ghostFiltered.map((g) => (
        <div
          key={`ghost-${keyOf(g.string, g.fret)}`}
          data-testid="upcoming-zone-preview"
          className={`absolute rounded-full ${size} flex items-center justify-center`}
          style={{
            left: `${noteX(g.fret)}%`,
            top: `${rowY(g.string)}%`,
            transform: "translate(-50%,-50%)",
            border: "1px dashed rgba(245,158,11,0.7)",
            background: "rgba(217,119,6,0.12)",
            opacity: 0.32 + transitionIntensity * 0.5,
            transition: "opacity 300ms ease",
          }}
        />
      ))}

      {/* zone notes */}
      {zone &&
        zone.notes.map((note) => {
          const k = keyOf(note.string, note.fret);
          const lit = litKeys.has(k);
          const isRoot = note.role === "root";
          const inRecovery = recoveryLevel > 0;
          const litRole = litMap.get(k)?.role;
          const isRecoveryNote = inRecovery && litRole === "recovery";

          if (inRecovery && !lit) return null; // recede during recovery

          const showLabel =
            isRoot || isRecoveryNote || (lit && mode === "FOLLOW");

          return (
            <NoteMarker
              key={k}
              note={note}
              x={noteX(note.fret)}
              y={rowY(note.string)}
              sizeClass={size}
              lit={lit}
              isRoot={isRoot}
              recovery={isRecoveryNote}
              showLabel={showLabel}
              onTap={onNoteTap}
            />
          );
        })}

      {/* fret numbers */}
      <div className="absolute inset-x-0 bottom-0 flex justify-between px-1 pointer-events-none">
        {Array.from({ length: NUM_FRETS }).map((_, i) => (
          <span
            key={`num-${i}`}
            className="text-[9px] font-mono text-slate-500/70"
            style={{ width: `${100 / NUM_FRETS}%`, textAlign: "center" }}
          >
            {FRET_MIN + i}
          </span>
        ))}
      </div>
    </div>
  );
};

const ZoneGlow: React.FC<{ zone: Zone }> = ({ zone }) => {
  const frets = zone.notes.map((n) => n.fret);
  const min = Math.min(...frets);
  const max = Math.max(...frets);
  const left = ((min - FRET_MIN) / NUM_FRETS) * 100;
  const width = ((max - min + 1) / NUM_FRETS) * 100;
  return (
    <motion.div
      key={zone.id}
      data-testid="safe-zone-container"
      className="absolute top-[4%] bottom-[10%] rounded-2xl"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.9, ease: "easeOut" }}
      style={{
        left: `${left}%`,
        width: `${width}%`,
        background:
          "radial-gradient(ellipse at center, rgba(245,158,11,0.14) 0%, rgba(245,158,11,0.05) 55%, rgba(245,158,11,0) 100%)",
        boxShadow: "inset 0 0 40px rgba(245,158,11,0.08)",
      }}
    />
  );
};

interface NoteMarkerProps {
  note: FretNote;
  x: number;
  y: number;
  sizeClass: string;
  lit: boolean;
  isRoot: boolean;
  recovery: boolean;
  showLabel: boolean;
  onTap?: (n: FretNote) => void;
}

const NoteMarker: React.FC<NoteMarkerProps> = ({
  note,
  x,
  y,
  sizeClass,
  lit,
  isRoot,
  recovery,
  showLabel,
  onTap,
}) => {
  let style: React.CSSProperties = {
    left: `${x}%`,
    top: `${y}%`,
    transform: "translate(-50%,-50%)",
  };
  let bg = "transparent";
  let border = "1px solid rgba(148,163,184,0.25)";
  let boxShadow = "none";
  let color = "rgba(226,232,240,0.85)";
  let opacity = 0.35;

  if (recovery) {
    bg = "radial-gradient(circle at 35% 30%, #FDE68A, #F59E0B 70%)";
    boxShadow = "0 0 22px rgba(245,158,11,0.75), 0 0 8px rgba(56,189,248,0.5)";
    border = "1px solid rgba(253,230,138,0.9)";
    color = "#1a1204";
    opacity = 1;
  } else if (lit && isRoot) {
    bg = "linear-gradient(135deg,#FDE68A 0%,#F59E0B 55%,#D97706 100%)";
    boxShadow = "0 0 18px rgba(245,158,11,0.6)";
    border = "2px solid rgba(255,241,204,0.9)";
    color = "#241701";
    opacity = 1;
  } else if (lit && note.role !== "passing") {
    bg = "rgba(245,158,11,0.9)";
    boxShadow = "0 0 14px rgba(245,158,11,0.45)";
    border = "1px solid rgba(253,230,138,0.7)";
    color = "#241701";
    opacity = 1;
  } else if (lit) {
    // passing note, lit but softer (outlined)
    bg = "rgba(245,158,11,0.12)";
    border = "1.5px solid rgba(245,158,11,0.7)";
    color = "#FDE68A";
    opacity = 0.95;
  }

  const marker = (
    <motion.button
      type="button"
      data-testid={`fret-note-${note.string}-${note.fret}`}
      onClick={onTap ? () => onTap(note) : undefined}
      className={`absolute rounded-full ${sizeClass} flex items-center justify-center font-mono font-semibold`}
      style={{ ...style, background: bg, border, boxShadow, color, cursor: onTap ? "pointer" : "default" }}
      animate={
        recovery
          ? { scale: [1, 1.16, 1], opacity: [1, 0.92, 1] }
          : { opacity, scale: 1 }
      }
      transition={
        recovery
          ? { duration: 60 / 90 * 2, repeat: Infinity, ease: "easeInOut" }
          : { duration: 0.5, ease: "easeOut" }
      }
      whileTap={onTap ? { scale: 0.9 } : undefined}
    >
      {showLabel ? note.note : ""}
    </motion.button>
  );
  return marker;
};
